import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Box, Backdrop, Stack } from "@mui/material";
import ErrorBox from "./ErrorBox";
import InputFilesContext from "./AsyncFormBox/InputFilesContext";
import FormContext from "./AsyncFormBox/FormContext";
import ScrollUtils from "../../utils/ScrollUtils";

function getPendingChild(progress) {
  return (
    <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
      <Stack
        spacing={2}
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.4);",
          borderRadius: 2,
          p: 10,
        }}>
        <CircularProgress color="inherit" />
        {progress}
      </Stack>
    </Backdrop>
  );
}

AsyncFormBox.propTypes = {
  boxProps: PropTypes.object,
  userError: ErrorBox.propTypes.userError,
  children: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  onSubmitDone: PropTypes.func,
  onSubmitFail: PropTypes.func,
  onValidate: PropTypes.func,
  progress: PropTypes.node,
  translateError: PropTypes.func,
  formValues: PropTypes.object,
};

function AsyncFormBox({
  boxProps,
  userError,
  children,
  onSubmit,
  onSubmitDone,
  onSubmitFail,
  onValidate,
  progress,
  translateError,
  formValues,
}) {
  const [feedback, setFeedback] = useState({});
  const formValuesByRef = useRef(formValues || {});
  const formValidationsRef = useRef({});
  const formBoxRef = useRef(null);

  // TODO refactor inputFiles handling into the ref, including the handlers + FileInput component
  const [inputFiles, setInputFiles] = useState({});

  async function handleSubmit(event) {
    event.preventDefault();
    const formValues = extractFormValues(event);

    const validationResult = await validateForm(formValues, event);
    if (!validationResult) return;

    setFeedback({ state: "pending" });

    let result;
    try {
      result = await onSubmit(formValues, event);
    } catch (error) {
      console.error("Error during processing onSubmit callback", error);
      setFeedback({ state: "error", error });
      onSubmitFail && (await onSubmitFail(error));
      ScrollUtils.scrollToElement(formBoxRef.current);
      return;
    }

    setFeedback({ state: "success", result });
    onSubmitDone && (await onSubmitDone(result, formValues));
  }

  function extractFormValues(event) {
    const values = {
      ...inputFiles,
    };

    const data = new FormData(event.currentTarget);
    for (const key of data.keys()) {
      const value = data.get(key);
      if (value instanceof File) continue; // files from react-dropzone are not available, they are empty

      values[key] = value;
    }

    // merge custom values
    Object.assign(values, formValuesByRef.current);

    return values;
  }

  async function validateForm(formValues, event) {
    if (onValidate) {
      const validationResult = await onValidate(formValues, event);
      if (!validationResult) return false;
    }

    for (const inputKey of Object.keys(formValidationsRef.current)) {
      const validationFunc = formValidationsRef.current[inputKey];
      const validationResult = await validationFunc(formValues, event, inputKey);
      if (!validationResult) return false;
    }

    return true;
  }

  function addFiles(name, files) {
    setInputFiles((inputFiles) => {
      inputFiles[name] = files;
      return inputFiles;
    });
  }

  function removeFiles(name) {
    setInputFiles((inputFiles) => {
      delete inputFiles[name];
      return inputFiles;
    });
  }

  function setValueByRef(name, value) {
    formValuesByRef.current[name] = value;
  }

  function setValuesByRef(values) {
    formValuesByRef.current = values;
  }

  function registerValidation(inputName, validationFunc) {
    formValidationsRef.current[inputName] = validationFunc;

    return () => {
      delete formValidationsRef.current[inputName];
    };
  }

  function getTranslatedError() {
    const translatedError = translateError?.(feedback.error);

    if (typeof translatedError === "string") {
      const userError = {
        message: translatedError,
      };
      return <ErrorBox userError={userError} sx={{ mb: 2 }} />;
    } else if (typeof translatedError === "object" && translatedError?.message) {
      const userError = {
        title: translatedError.title,
        message: translatedError.message,
      };
      return <ErrorBox userError={userError} sx={{ mb: 2 }} />;
    } else {
      return translatedError || <ErrorBox error={feedback.error} sx={{ mb: 2 }} />;
    }
  }

  const formContextValue = {
    values: formValuesByRef.current,
    setValue: setValueByRef,
    setValues: setValuesByRef,
    registerValidation,
  };
  return (
    <React.Fragment>
      {feedback.state === "pending" && getPendingChild(progress)}

      <Box ref={formBoxRef} component={"form"} onSubmit={handleSubmit} {...boxProps}>
        {feedback.state === "error" && getTranslatedError()}

        {userError && <ErrorBox userError={userError} sx={{ mb: 2 }} />}

        <FormContext.Provider value={formContextValue}>
          <InputFilesContext.Provider value={{ addFiles, removeFiles }}>{children}</InputFilesContext.Provider>
        </FormContext.Provider>
      </Box>
    </React.Fragment>
  );
}

export default AsyncFormBox;
